import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Box, Paper, Typography, Divider, Stack, Fade } from "@mui/material";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { logger } from "../../utils/logger";
import MarkdownRenderer from "./MarkdownRenderer";
import PDFViewer from "./PDFViewer";
import PostmanViewer from "./PostmanViewer";
import BlockRenderer from "./blocks/BlockRenderer";

const isScrollable = (el) => {
  if (!el) return false;
  const style = getComputedStyle(el);
  const overflowY = style.overflowY;
  return (
    (overflowY === "auto" || overflowY === "scroll") &&
    el.scrollHeight > el.clientHeight
  );
};

const findScrollContainer = (startEl) => {
  let el = startEl;
  while (el && el !== document.body && el !== document.documentElement) {
    if (isScrollable(el)) return el;
    el = el.parentElement;
  }
  return null;
};

const UniversalContentRenderer = ({
  menuFileRef,
  uri,
  scrollContainerRefProp,
  basePath,
  getResourceUrl,
}) => {
  const language = useSelector((state) => state.language.activeLanguage);
  const location = useLocation();
  const navigate = useNavigate();

  const [isMulti, setIsMulti] = useState(false);

  const [single, setSingle] = useState({
    title: "",
    kind: null,
    data: null,
  });

  const [multi, setMulti] = useState({
    title: "",
    index: false,
    items: [],
  });

  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState(null);

  const rootRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const sectionRefs = useRef([]);
  const isNavigatingRef = useRef(false);
  const rafIdRef = useRef(null);
  const isFirstLoadWithHashRef = useRef(true);
  const [activeContentIndex, setActiveContentIndex] = useState(0);
  const currentHash = useMemo(
    () => (location.hash ? location.hash.substring(1) : ""),
    [location.hash],
  );

  const buildResourceUrl = useCallback((path) => {
    if (getResourceUrl) {
      return getResourceUrl(path);
    }

    if (path.startsWith('/')) {
      return '.' + path;
    }
    return path;
  }, [getResourceUrl]);

  const flattenEntries = (entries) => {
    let result = [];
    for (const entry of entries) {
      if (entry) {
        result.push(entry);
        if (entry.entries) {
          result = result.concat(flattenEntries(entry.entries));
        }
      }
    }
    return result;
  };

  const findEntryInMenuSingle = useCallback((menu, targetUri) => {
    if (!menu?.menu) return null;
    const normalizedTargetUri = targetUri.replace(/^\/+|\/+$/g, "");
    const allEntries = menu.menu.flatMap((section) =>
      flattenEntries(section.entries || []),
    );

    let found = allEntries.find(
      (entry) =>
        entry?.uri &&
        entry.uri.replace(/^\/+|\/+$/g, "") === normalizedTargetUri,
    );

    if (!found) {
      found = allEntries.find((entry) => {
        if (!entry?.uri) return false;
        const entryUri = entry.uri.replace(/^\/+|\/+$/g, "");
        return (
          normalizedTargetUri.endsWith(entryUri) ||
          entryUri.endsWith(normalizedTargetUri)
        );
      });
    }

    return found || allEntries.find((entry) => entry?.fileRef);
  }, []);

  const findEntryInMenuMulti = useCallback((menu, targetUri) => {
    if (!menu?.menu) return null;
    const normalizedTargetUri = targetUri.replace(/^\/+|\/+$/g, "");
    const allEntries = menu.menu.flatMap((section) =>
      flattenEntries(section.entries || []),
    );
    return allEntries.find(
      (entry) =>
        entry?.uri &&
        entry.uri.replace(/^\/+|\/+$/g, "") === normalizedTargetUri,
    );
  }, []);

  const makeAnchor = (title) =>
    (title || "")
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");

  const renderUnavailableBox = (title) => (
    <Paper className="unavailable-box">
      <Typography variant="h6" className="unavailable-title">
        {title || "Contenuto non disponibile"}
      </Typography>
      <Typography variant="body2" className="unavailable-message">
        Il contenuto richiesto non è disponibile.
      </Typography>
    </Paper>
  );

  const isValidMarkdown = (text) =>
    typeof text === "string" && text.trim().length > 0;
  const isValidUrl = (url) => typeof url === "string" && url.trim().length > 0;
  const isValidJsonBlocks = (json) => {
    if (!json || typeof json !== "object") return false;
    if (!Array.isArray(json.body)) return false;
    return json.body.every(
      (block) => block && typeof block === "object" && !!block.type,
    );
  };

  const safeFetchText = async (url, expectedTypes) => {
    try {
      const fullUrl = buildResourceUrl(url);
      const res = await fetch(fullUrl, { cache: "no-cache" });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      return await res.text();
    } catch (error) {
      console.error(`Errore nel caricamento di ${url}:`, error);
      throw error;
    }
  };

  const safeFetchJson = async (url, expectedTypes) => {
    const fullUrl = buildResourceUrl(url);
    const res = await fetch(fullUrl, { cache: "no-cache" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const ct = res.headers.get("content-type") || "";
    const matches = expectedTypes.some((t) => ct.includes(t));
    if (!matches) {
      throw new Error(`Content-Type non valido: ${ct}`);
    }
    return res.json();
  };

  const loadContentFromFileRef = useCallback(
    async (fileRef, languageParam) => {
      const filePath = `./content/${languageParam}/${fileRef.path}`;

      const makeUnavailable = (reason) => ({
        type: "unavailable",
        title: fileRef.title,
        anchor: makeAnchor(fileRef.title),
        reason,
      });

      try {
        const anchor = makeAnchor(fileRef.title);

        if (fileRef.path.endsWith(".md")) {
          const text = await safeFetchText(filePath, [
            "text/markdown",
            "text/plain",
            "text/x-markdown",
            "application/octet-stream",
          ]);
          if (!isValidMarkdown(text)) return makeUnavailable("Markdown vuoto");
          return {
            type: "markdown",
            title: fileRef.title,
            content: text,
            filePath: buildResourceUrl(filePath),
            anchor,
          };
        } else if (fileRef.path.endsWith(".pdf")) {
          const fullUrl = buildResourceUrl(filePath);
          if (!isValidUrl(fullUrl))
            return makeUnavailable("PDF URL non valido");
          return {
            type: "pdf",
            title: fileRef.title,
            url: fullUrl,
            fileName: fileRef.title,
            anchor,
          };
        } else if (
          fileRef.path.includes("postman") ||
          fileRef.path.endsWith(".json")
        ) {
          if (fileRef.path.includes("postman")) {
            try {
              await safeFetchJson(filePath, [
                "application/json",
                "application/vnd.postman.collection+json",
              ]);
            } catch (e) {
              return makeUnavailable(e.message);
            }
            return {
              type: "postman",
              title: fileRef.title,
              url: buildResourceUrl(filePath),
              fileName: fileRef.title,
              anchor,
            };
          } else {
            const json = await safeFetchJson(filePath, ["application/json"]);
            if (!isValidJsonBlocks(json))
              return makeUnavailable("JSON non valido");
            return {
              type: "json",
              title: fileRef.title || json?.title,
              content: json,
              anchor,
            };
          }
        }

        return makeUnavailable("Tipo non supportato");
      } catch (error) {
        logger.error(`Error loading ${fileRef.path}:`, error);
        return makeUnavailable(error.message);
      }
    },
    [buildResourceUrl]
  );

  useEffect(() => {
    if (!isMulti) return;
    const provided = scrollContainerRefProp?.current;
    if (provided) {
      scrollContainerRef.current = provided;
      return;
    }
    if (!rootRef.current) return;
    const container = findScrollContainer(rootRef.current) || null;
    scrollContainerRef.current = container;
  }, [scrollContainerRefProp, uri, isMulti]);

  useEffect(() => {
    if (!isMulti) return;
    const container = scrollContainerRef.current;
    const target = container || window;

    const onScroll = () => {
      if (isNavigatingRef.current) return;
      if (!sectionRefs.current?.length) return;

      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = requestAnimationFrame(() => {
        const midY = container
          ? container.getBoundingClientRect().top + container.clientHeight / 2
          : window.innerHeight / 2;

        let idx = 0;
        for (let i = sectionRefs.current.length - 1; i >= 0; i--) {
          const el = sectionRefs.current[i];
          if (!el) continue;
          const rect = el.getBoundingClientRect();
          if (rect.top <= midY) {
            idx = i;
            break;
          }
        }

        if (idx !== activeContentIndex) {
          setActiveContentIndex(idx);
        }
      });
    };

    target.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      target.removeEventListener("scroll", onScroll);
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    };
  }, [
    activeContentIndex,
    multi.items,
    currentHash,
    location.pathname,
    navigate,
    scrollContainerRefProp,
    uri,
    isMulti,
  ]);

  useEffect(() => {
    if (!isMulti) return;
    const items = multi.items;
    if (!currentHash || !items.length) return;
    if (!isFirstLoadWithHashRef.current) return;

    const targetIndex = items.findIndex((c) => c.anchor === currentHash);
    if (targetIndex === -1) {
      isFirstLoadWithHashRef.current = false;
      return;
    }

    if (targetIndex !== activeContentIndex) {
      setActiveContentIndex(targetIndex);
    }

    const el = sectionRefs.current[targetIndex];
    if (!el) {
      isFirstLoadWithHashRef.current = false;
      return;
    }

    const container = scrollContainerRef.current;
    isNavigatingRef.current = true;

    if (container) {
      const crect = container.getBoundingClientRect();
      const rect = el.getBoundingClientRect();
      const delta = rect.top - crect.top;
      container.scrollTo({
        top: container.scrollTop + delta,
        behavior: "smooth",
      });
    } else {
      el.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    }

    const unlock = setTimeout(() => {
      isNavigatingRef.current = false;
      isFirstLoadWithHashRef.current = false;
    }, 600);
    return () => clearTimeout(unlock);
  }, [
    currentHash,
    multi.items,
    activeContentIndex,
    scrollContainerRefProp,
    isMulti,
  ]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setPageError(null);

      if (!menuFileRef) {
        setLoading(false);
        setIsMulti(false);
        setSingle({
          title: "Contenuto non disponibile",
          kind: "unavailable",
          data: null,
        });
        return;
      }

      try {
        const menuUrl = buildResourceUrl(`./config/${language}/${menuFileRef}`);

        let menuResponse;
        try {
          menuResponse = await fetch(menuUrl, {
            cache: "no-cache",
            headers: {
              'Accept': 'application/json'
            }
          });
        } catch (fetchErr) {
          throw new Error(`Network error fetching menu: ${fetchErr.message}`);
        }

        if (!menuResponse.ok) {
          throw new Error(`Menu not found: ${menuResponse.status} ${menuResponse.statusText}`);
        }

        let menu;
        try {
          menu = await menuResponse.json();
        } catch (jsonErr) {
          throw new Error(`Invalid JSON in menu: ${jsonErr.message}`);
        }

        const entry = findEntryInMenuSingle(menu, uri);
        if (!entry) {
          throw new Error(`Entry not found for URI: ${uri}`);
        }

        document.title = entry.title || "Documentazione";

        if (Array.isArray(entry.fileRef)) {
          setIsMulti(true);

          const entryExact = findEntryInMenuMulti(menu, uri) || entry;

          const items = [];
          for (const fileRef of entryExact.fileRef) {
            const content = await loadContentFromFileRef(fileRef, language);
            items.push(content);
          }

          const packed = { title: entryExact.title, index: entryExact.index, items };
          setMulti(packed);
          sectionRefs.current = new Array(items.length);
        } else {
          setIsMulti(false);

          const filePath = `./content/${language}/${entry.fileRef}`;
          let payload = { title: entry.title, kind: null, data: null };

          if (entry.fileRef.endsWith(".md")) {
            try {
              const text = await safeFetchText(filePath, [
                "text/markdown",
                "text/plain",
                "text/x-markdown",
                "application/octet-stream",
              ]);
              payload.kind = isValidMarkdown(text) ? "markdown" : "unavailable";
              payload.data =
                payload.kind === "markdown" ? { markdown: text } : null;
            } catch (err) {
              payload.kind = "unavailable";
              payload.data = null;
            }
          } else if (entry.fileRef.endsWith(".pdf")) {
            try {
              const fullUrl = buildResourceUrl(filePath);
              payload.kind = isValidUrl(fullUrl) ? "pdf" : "unavailable";
              payload.data =
                payload.kind === "pdf"
                  ? {
                      url: fullUrl,
                      fileName: entry.title || filePath.split("/").pop(),
                    }
                  : null;
            } catch (err) {
              payload.kind = "unavailable";
              payload.data = null;
            }
          } else if (
            entry.fileRef.includes("postman") ||
            entry.fileRef.endsWith(".json")
          ) {
            if (entry.fileRef.includes("postman")) {
              try {
                await safeFetchJson(filePath, [
                  "application/json",
                  "application/vnd.postman.collection+json",
                ]);
                payload.kind = "postman";
                payload.data = {
                  url: buildResourceUrl(filePath),
                  fileName: entry.title || filePath.split("/").pop(),
                };
              } catch (e) {
                payload.kind = "unavailable";
                payload.data = null;
              }
            } else {
              try {
                const json = await safeFetchJson(filePath, ["application/json"]);
                payload.kind = isValidJsonBlocks(json) ? "json" : "unavailable";
                payload.data = payload.kind === "json" ? { json } : null;
                if (payload.kind === "json" && json?.title) {
                  payload.title = json.title;
                }
              } catch (err) {
                payload.kind = "unavailable";
                payload.data = null;
              }
            }
          } else {
            payload.kind = "unavailable";
          }

          setSingle(payload);
        }
      } catch (err) {
        setPageError(err.message);
        setIsMulti(false);
        setSingle({
          title: `Contenuto non disponibile (${err.message})`,
          kind: "unavailable",
          data: null,
        });
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [
    uri,
    language,
    menuFileRef,
    findEntryInMenuSingle,
    findEntryInMenuMulti,
    loadContentFromFileRef,
    buildResourceUrl,
    basePath,
  ]);

  const renderMultiItem = (content) => {
    switch (content.type) {
      case "markdown":
        return <MarkdownRenderer content={content.content} />;
      case "pdf":
        return <PDFViewer pdfUrl={content.url} fileName={content.fileName} />;
      case "postman":
        return (
          <PostmanViewer
            collectionUrl={content.url}
            fileName={content.fileName}
          />
        );
      case "json":
        return (
          <>
            {content.content?.body?.map((block, index) => (
              <BlockRenderer key={`block-${index}`} block={block} />
            ))}
          </>
        );
      case "unavailable":
      default:
        return renderUnavailableBox(content.title);
    }
  };

  const handleIndexClick = (index, anchor) => {
    if (multi.items[index]?.anchor === currentHash) return;
    navigate(`${location.pathname}#${anchor}`, { replace: true });

    const el = sectionRefs.current[index];
    if (!el) return;

    const container = scrollContainerRef.current;
    isNavigatingRef.current = true;

    if (container) {
      const crect = container.getBoundingClientRect();
      const rect = el.getBoundingClientRect();
      const delta = rect.top - crect.top;
      container.scrollTo({
        top: container.scrollTop + delta,
        behavior: "smooth",
      });
    } else {
      el.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    }

    setTimeout(() => {
      isNavigatingRef.current = false;
    }, 800);
  };

  if (loading) {
    return (
      <Box ref={rootRef} className="loading-container">
        <Typography variant="h6" className="loading-text">
          Caricamento contenuti...
        </Typography>
      </Box>
    );
  }

  if (pageError && !isMulti && single.kind === "unavailable") {
    return (
      <Stack ref={rootRef} className="error-container">
        <Typography variant="h4" component="h1" className="error-title">
          {single.title || "Contenuto non disponibile"}
        </Typography>
        {renderUnavailableBox(single.title)}
      </Stack>
    );
  }

  if (isMulti) {
    if (!multi.items.length) {
      return (
        <Box ref={rootRef}>
          {renderUnavailableBox(multi.title || "Contenuto non disponibile")}
        </Box>
      );
    }

    return (
      <Stack ref={rootRef} sx={{ width: "100%" }} spacing={2}>
        {multi.items.length > 1 && multi?.index && (
          <Paper className="index-paper">
            <Typography variant="h6" className="index-title">
              {multi.title}
            </Typography>
            <Stack className="index-list">
              {multi.items.map((content, index) => (
                <Box key={index} className="index-item">
                  <Typography
                    component="a"
                    href={`#${content.anchor}`}
                    onClick={(e) => {
                      e.preventDefault();
                      handleIndexClick(index, content.anchor);
                    }}
                    className={`index-link ${activeContentIndex === index ? 'index-link-active' : ''}`}
                  >
                    {index + 1}. {content.title}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Paper>
        )}

        {multi.items.map((content, index) => (
          <Fade in={true} timeout={300 + index * 100} key={index}>
            <Box
              ref={(el) => (sectionRefs.current[index] = el)}
              id={content.anchor}
              className="content-item"
            >
              <Box sx={{ mb: 0 }}>{renderMultiItem(content)}</Box>
            </Box>
          </Fade>
        ))}
      </Stack>
    );
  }

  if (single.kind === "unavailable" || !single.kind) {
    return (
      <Stack className="error-container">
        <Typography variant="h4" component="h1" className="error-title">
          {single.title || "Contenuto non disponibile"}
        </Typography>
        {renderUnavailableBox(single.title)}
      </Stack>
    );
  }

  return (
    <Stack sx={{ width: "100%", scrollBehavior: "smooth" }}>
      {single.kind === "markdown" && (
        <MarkdownRenderer content={single.data?.markdown} />
      )}

      {single.kind === "pdf" && (
        <>
          <Typography variant="h4" component="h1" className="single-content-title">
            {single.title}
          </Typography>
          <PDFViewer
            pdfUrl={single.data?.url}
            fileName={single.data?.fileName}
          />
        </>
      )}

      {single.kind === "postman" && (
        <>
          <Typography variant="h4" component="h1" className="single-content-title">
            {single.title}
          </Typography>
          <PostmanViewer
            collectionUrl={single.data?.url}
            fileName={single.data?.fileName}
          />
        </>
      )}

      {single.kind === "json" && single.data?.json && (
        <>
          <Typography variant="h4" component="h1" className="single-content-title">
            {single.data?.json?.title || single.title}
          </Typography>
          {single.data?.json?.body?.map((block, index) => (
            <BlockRenderer key={`block-${index}`} block={block} />
          ))}
        </>
      )}
    </Stack>
  );
};

export default UniversalContentRenderer;